/**
 * Shared formatting helpers for the billing page. Kept in one place so the plan
 * hero, usage grid and comparison table format numbers, bytes, prices and
 * limits identically.
 */

const numberFormatter = new Intl.NumberFormat("en-US");

// Mirrors UNLIMITED in the server plan-config: any limit at/above this sentinel
// is shown as "Unlimited" instead of a giant number.
export const UNLIMITED_THRESHOLD = 1_000_000_000;

/**
 * Whether a limit is the "no real ceiling" sentinel.
 *
 * The sentinel is a **count**: 1e9 is past any plausible seat, channel or post
 * ceiling. It is nowhere near past a *byte* ceiling — the Agency plan's 50 GB
 * is 5.4e10 — so putting a storage limit through this threshold reports
 * "Unlimited" for a quota that is entirely finite, and drops storage out of the
 * "limits you have reached" count on the billing page. Every plan's
 * `maxMediaStorageBytes` is a real number (250 MB through 50 GB), so a byte
 * limit is never unlimited by threshold and the unit has to be declared.
 */
export function isUnlimited(limit: number, unit: "bytes" | "count" = "count") {
  return unit !== "bytes" && limit >= UNLIMITED_THRESHOLD;
}

export function formatNumber(value: number) {
  return numberFormatter.format(value);
}

/** A limit as a display string — "Unlimited" for the sentinel, else the count. */
export function formatCount(limit: number) {
  return isUnlimited(limit) ? "Unlimited" : numberFormatter.format(limit);
}

export function formatBytes(bytes: number) {
  if (bytes >= 1024 * 1024 * 1024) {
    return `${numberFormatter.format(
      Math.round((bytes / (1024 * 1024 * 1024)) * 10) / 10,
    )} GB`;
  }

  return `${numberFormatter.format(Math.round(bytes / (1024 * 1024)))} MB`;
}

/** Cents → a dollar string without the currency symbol ("9", "8.50"). */
export function formatUsd(cents: number) {
  const dollars = cents / 100;

  return Number.isInteger(dollars)
    ? dollars.toLocaleString("en-US")
    : dollars.toLocaleString("en-US", {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      });
}

/** Clamped 0–100 usage percentage. A zero/negative limit reads as full. */
export function usagePercent(current: number, limit: number) {
  if (isUnlimited(limit)) {
    // Unlimited plans still show a small, non-empty bar for visual weight.
    return current > 0 ? 6 : 0;
  }

  if (limit <= 0) {
    return current > 0 ? 100 : 0;
  }

  return Math.min(100, Math.round((current / limit) * 100));
}

/** "5 / mo" for a finite monthly cap, "Unlimited" for the sentinel. */
export function formatMonthlyLimit(limit: number) {
  return isUnlimited(limit)
    ? "Unlimited"
    : `${numberFormatter.format(limit)} / mo`;
}
